import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth";

import Layout from "@/components/Layout";
import RequireRole from "@/components/RequireRole";
import RoleHome from "@/components/RoleHome";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Products from "@/pages/Products";
import ProductForm from "@/pages/ProductForm";
import ProductDetail from "@/pages/ProductDetail";
import StockEntry from "@/pages/StockEntry";
import StockDecrease from "@/pages/StockDecrease";
import StockMovements from "@/pages/StockMovements";
import CriticalStock from "@/pages/CriticalStock";
import Companies from "@/pages/Companies";
import CompanyForm from "@/pages/CompanyForm";
import Quotes from "@/pages/Quotes";
import QuoteForm from "@/pages/QuoteForm";
import Orders from "@/pages/Orders";
import CariTakip from "@/pages/CariTakip";
import PaymentEntry from "@/pages/PaymentEntry";
import CariMovements from "@/pages/CariMovements";
import AgingReport from "@/pages/AgingReport";
import StockReports from "@/pages/StockReports";
import SalesReports from "@/pages/SalesReports";
import Definitions from "@/pages/Definitions";
import Personnel from "@/pages/Personnel";
import Notifications from "@/pages/Notifications";
import Settings from "@/pages/Settings";
import QuoteDecision from "@/pages/QuoteDecision";
import AuditLogs from "@/pages/AuditLogs";
import ActivityReports from "@/pages/ActivityReports";
import Expenses from "@/pages/Expenses";
import WorkPlans from "@/pages/WorkPlans";
import SalesCompaniesReport from "@/pages/SalesCompaniesReport";
import Assignments from "@/pages/Assignments";
import EcommerceSales from "@/pages/EcommerceSales";
import EcommerceSaleForm from "@/pages/EcommerceSaleForm";
import EcommerceMarketplaces from "@/pages/EcommerceMarketplaces";
import PurchaseOrders from "@/pages/PurchaseOrders";
import PurchaseOrderForm from "@/pages/PurchaseOrderForm";
import Productions from "@/pages/Productions";

const WH = ["admin", "warehouse"];
const SALES = ["admin", "sales"];
const ACCT = ["admin", "accounting"];

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/q/:token" element={<QuoteDecision />} />
          <Route path="/" element={<Layout />}>
            <Route index element={<RoleHome />} />
            <Route path="dashboard" element={<RequireRole roles={["admin"]}><Dashboard /></RequireRole>} />

            <Route path="products" element={<Products />} />
            <Route path="products/new" element={<RequireRole roles={WH}><ProductForm /></RequireRole>} />
            <Route path="products/:id" element={<ProductDetail />} />
            <Route path="products/:id/edit" element={<RequireRole roles={WH}><ProductForm /></RequireRole>} />

            <Route path="stock/entry" element={<RequireRole roles={WH}><StockEntry /></RequireRole>} />
            <Route path="stock/decrease" element={<RequireRole roles={WH}><StockDecrease /></RequireRole>} />
            <Route path="stock/movements" element={<StockMovements />} />
            <Route path="stock/critical" element={<CriticalStock />} />

            <Route path="companies" element={<RequireRole roles={["admin","sales","accounting"]}><Companies /></RequireRole>} />
            <Route path="companies/new" element={<RequireRole roles={SALES}><CompanyForm /></RequireRole>} />
            <Route path="companies/:id/edit" element={<RequireRole roles={SALES}><CompanyForm /></RequireRole>} />

            <Route path="quotes" element={<RequireRole roles={SALES}><Quotes /></RequireRole>} />
            <Route path="quotes/new" element={<RequireRole roles={SALES}><QuoteForm /></RequireRole>} />
            <Route path="quotes/:id/edit" element={<RequireRole roles={SALES}><QuoteForm /></RequireRole>} />

            <Route path="orders" element={<RequireRole roles={SALES}><Orders /></RequireRole>} />
            <Route path="orders/new" element={<RequireRole roles={SALES}><QuoteForm mode="order" /></RequireRole>} />

            <Route path="cari" element={<RequireRole roles={ACCT}><CariTakip /></RequireRole>} />
            <Route path="cari/movements" element={<RequireRole roles={ACCT}><CariMovements /></RequireRole>} />
            <Route path="cari/aging" element={<RequireRole roles={["admin","accounting","viewer"]}><AgingReport /></RequireRole>} />
            <Route path="payments/new" element={<RequireRole roles={ACCT}><PaymentEntry /></RequireRole>} />

            <Route path="reports/stock" element={<StockReports />} />
            <Route path="reports/sales" element={<SalesReports />} />
            <Route path="reports/cari" element={<RequireRole roles={["admin","accounting","viewer"]}><AgingReport /></RequireRole>} />
            <Route path="reports/sales-companies" element={<RequireRole roles={["admin"]}><SalesCompaniesReport /></RequireRole>} />

            <Route path="definitions/:kind" element={<RequireRole roles={["admin"]}><Definitions /></RequireRole>} />

            <Route path="personnel" element={<RequireRole roles={["admin"]}><Personnel /></RequireRole>} />
            <Route path="assignments" element={<RequireRole roles={["admin","accounting"]}><Assignments /></RequireRole>} />
            <Route path="ecommerce/sales" element={<RequireRole roles={["admin","sales","accounting"]}><EcommerceSales /></RequireRole>} />
            <Route path="ecommerce/sales/new" element={<RequireRole roles={["admin","sales","accounting"]}><EcommerceSaleForm /></RequireRole>} />
            <Route path="ecommerce/sales/:id/edit" element={<RequireRole roles={["admin","sales","accounting"]}><EcommerceSaleForm /></RequireRole>} />
            <Route path="definitions/marketplaces" element={<RequireRole roles={["admin"]}><EcommerceMarketplaces /></RequireRole>} />
            <Route path="purchase-orders" element={<RequireRole roles={["admin","warehouse","accounting"]}><PurchaseOrders /></RequireRole>} />
            <Route path="purchase-orders/new" element={<RequireRole roles={["admin","warehouse","accounting"]}><PurchaseOrderForm /></RequireRole>} />
            <Route path="purchase-orders/:id/edit" element={<RequireRole roles={["admin","warehouse","accounting"]}><PurchaseOrderForm /></RequireRole>} />
            <Route path="productions" element={<RequireRole roles={["admin","warehouse"]}><Productions /></RequireRole>} />
            <Route path="audit-logs" element={<RequireRole roles={["admin"]}><AuditLogs /></RequireRole>} />
            <Route path="activity-reports" element={<ActivityReports />} />
            <Route path="expenses" element={<Expenses />} />
            <Route path="work-plans" element={<WorkPlans />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="settings" element={<Settings />} />
          </Route>
          <Route path="*" element={<RoleHome />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
